import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ConnectWelshareButton, Schemas, useWelshare } from "@welshare/react";
import { AlertTriangle, Download, Info, RefreshCw, Upload } from "lucide-react";
import React, { useState } from "react";
import { generateFhirQuestionnaireResponse } from "../lib/fhir-export";
import type { FindriscAnswers, FindriscResults } from "../types/findrisc";
import { ThankYouNotification } from "./ThankYouNotification";

interface ResultsScreenProps {
  results: FindriscResults;
  answers: FindriscAnswers;
  onRestart: () => void;
  onSave: () => void;
}

export const ResultsScreen: React.FC<ResultsScreenProps> = ({
  results,
  answers,
  onRestart,
  onSave,
}) => {
  const { toast } = useToast();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [studyConsentAccepted, setStudyConsentAccepted] = useState(false);
  const [responseId, setResponseId] = useState<string>(undefined);

  const {
    isConnected,
    openWallet,
    submitData,
    isDialogOpen,
    isSubmitting,
    storageKey,
  } = useWelshare({
    applicationId: import.meta.env.VITE_APP_ID,
    apiBaseUrl: "https://staging.wallet.welshare.app",
    callbacks: {
      onUploaded: (data: {uploadResults: {errors: string[], insertedUid: string}}) => {
        console.log("[on uploaded]", data);
        setResponseId(data.uploadResults.insertedUid);
      },
    },
  });

  const handleOpenConsentModal = () => {
    setShowConsentModal(true);
  };

  const handleConfirmExport = () => {
    setShowConsentModal(false);
    toast({
      title: "Results prepared for export",
      description: "Connect your Health Profile and hit the \"Save Result\" button.",
    });
    // Reset form state
    setTermsAccepted(true);
    setStudyConsentAccepted(true);
    openWallet();
  };

  const handleCancelExport = () => {
    setShowConsentModal(false);
    setTermsAccepted(false);
    setStudyConsentAccepted(false);
  };

  const downloadFhirResponse = () => {
    const response = generateFhirQuestionnaireResponse(answers, results);
    const blob = new Blob([JSON.stringify(response, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "findrisc-response.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getRiskBadgeVariant = () => {
    switch (results.riskCategory) {
      case "low-moderate":
        return "risk-low";
      case "high":
        return "risk-moderate";
      case "very-high":
        return "risk-high";
      default:
        return "risk-low";
    }
  };

  const getRiskIcon = () => {
    switch (results.riskCategory) {
      case "low-moderate":
        return <Info className="h-5 w-5 text-success" />;
      case "high":
      case "very-high":
        return <AlertTriangle className="h-5 w-5 text-warning" />;
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card className="shadow-elevated bg-gradient-card">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl text-primary flex items-center justify-center gap-2">
            {getRiskIcon()}
            Your FINDRISC Results
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center space-y-4">
            <div className="space-y-2">
              <div className="text-4xl font-bold text-primary">
                {results.totalScore}
              </div>
              <p className="text-sm text-muted-foreground">Total Points</p>
            </div>

            <div className="space-y-2">
              <Badge
                variant={getRiskBadgeVariant()}
                className="text-sm px-4 py-2"
              >
                {results.riskCategory === "low-moderate" &&
                  "Low to Moderate Risk"}
                {results.riskCategory === "high" && "High Risk"}
                {results.riskCategory === "very-high" && "Very High Risk"}
              </Badge>
              <p className="text-lg font-semibold">
                {results.riskPercentage} 10-year risk
              </p>
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              {results.interpretation}
            </AlertDescription>
          </Alert>

          <div className="flex justify-center">
            {responseId ? (
              <ThankYouNotification responseId={responseId} />
            ) : termsAccepted && studyConsentAccepted && !showConsentModal ? (
              isDialogOpen && isConnected ? (
                <Button
                  disabled={isSubmitting}
                  className="flex items-center gap-2 w-4/5"
                  onClick={() => {
                    const response = generateFhirQuestionnaireResponse(
                      answers,
                      results
                    );
                    console.log(response);
                    submitData(Schemas.QuestionnaireResponse, response)
                  }}
                >
                  <Upload className="h-4 w-4" />
                  {isSubmitting ? "Saving..." : "Save Result"}
                </Button>
              ) : (
                <ConnectWelshareButton openWallet={openWallet} />
              )
            ) : (
              <Button
                onClick={handleOpenConsentModal}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Save the result to your Health Profile
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={onRestart}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Retake Assessment
            </Button>

            <Button
              onClick={downloadFhirResponse}
              variant="secondary"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Download Results
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showConsentModal} onOpenChange={setShowConsentModal}>
        <DialogContent className="max-w-md mx-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-center">
              Health Data Storage Consent
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Terms & Conditions - Required */}
            <div className="space-y-3">
              <h3 className="font-medium">1. Agree to T&Cs & Privacy Policy</h3>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(checked) =>
                    setTermsAccepted(checked === true)
                  }
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm leading-relaxed">
                  I agree to the{" "}
                  <a
                    href="https://www.welshare.health/terms-hpmp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.welshare.health/privacy-hpmp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:no-underline"
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            </div>

            {/* Study Invitations - Optional */}
            <div className="space-y-3">
              <h3 className="font-medium">
                2. Consent for study invitations / recruitment notifications
                (optional)
              </h3>
              <p className="text-sm font-medium">
                Receive study invitations (optional)
              </p>
              <p className="text-sm text-muted-foreground">
                If you opt in, you may be contacted about relevant studies. We
                will not share your contact or identity with a sponsor unless
                you explicitly accept a specific invitation.
              </p>

              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm font-medium mb-2">
                  What happens if I opt in?
                </p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>
                    • You may receive emails/in-app messages about studies that
                    match your profile.
                  </li>
                  <li>
                    • If you click "I'm interested", we'll show you the details
                    and ask for separate consent to share your contact or to
                    pre-screen you — you control this per study.
                  </li>
                  <li>• You can opt out of invitations at any time.</li>
                </ul>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="study-consent"
                  checked={studyConsentAccepted}
                  onCheckedChange={(checked) =>
                    setStudyConsentAccepted(checked === true)
                  }
                  className="mt-1"
                />
                <label
                  htmlFor="study-consent"
                  className="text-sm leading-relaxed"
                >
                  I consent to receive study invitations from Welshare. You can
                  opt out anytime.
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleCancelExport}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExport}
              disabled={!termsAccepted}
              className="flex-1"
            >
              Confirm & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
